import { useTranslation } from 'react-i18next'
import { ContextNotification, Modal } from '@pandiafi/components'
import React, { FC } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Highlight } from '@pandiafi/design-system'

import { generateReservationLocationName } from '../../components/ReservationsList/reservationNaming.util'
import {
  formatDate,
  formatTimeRange,
  getWeekdayTranslationKey,
} from '../../utils/dateUtils'
import { useAPIGetReservationsSuspense } from '../../api/apiHooks/useAPIGetReservations'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'

type PaymentStatusProps = {
  status: 'success' | 'failed'
}

export const PaymentStatus: FC<PaymentStatusProps> = ({ status }) => {
  const { t } = useTranslation(['reservations'])
  const { t: tCommon } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentContractId } = useCurrentContract()

  const reservationId = searchParams.get('reservationId')
  const { data: reservations = [] } =
    useAPIGetReservationsSuspense(currentContractId)

  const reservation =
    reservationId && status === 'success'
      ? reservations.find((r) => r.id === Number(reservationId))
      : null

  const onClose = () => {
    navigate(location.pathname, { replace: true })
  }

  return (
    <Modal
      show
      onClose={onClose}
      title={t('reservations:cancelModal.reservationTitle')}
      headerElement={
        <Highlight>{t('reservations:payment.singleReservation')}</Highlight>
      }
    >
      <Modal.Notifications>
        {status === 'success' && (
          <ContextNotification type="success" textAlign="center">
            <div>{t('reservations:payment.succeeded')}</div>
            {reservation && (
              <div className="mt-4 font-semibold">
                {generateReservationLocationName(reservation.calendarMeta, t)}
                {', '}
                {!reservation.recurring ? (
                  <>
                    {t(getWeekdayTranslationKey(reservation.startsAt))}{' '}
                    {formatDate(reservation.startsAt)}
                    {', '}
                    {formatTimeRange(reservation.startsAt, reservation.endsAt)}
                  </>
                ) : (
                  <>
                    {formatTimeRange(reservation.startsAt, reservation.endsAt)}
                    {', '}
                    {t('reservations:timeFormatting.beginningFrom')}{' '}
                    {formatDate(reservation.startsAt)}
                  </>
                )}
              </div>
            )}
          </ContextNotification>
        )}
        {status === 'failed' && (
          <ContextNotification type="error" textAlign="center">
            <div className="text-center">
              <strong>{t('reservations:payment.failed')}</strong>
              <div>{t('reservations:payment.failedMessage')}</div>
            </div>
          </ContextNotification>
        )}
      </Modal.Notifications>
      <Modal.Buttons>
        <Button variant="cancel" onClick={onClose}>
          {tCommon('closeButton')}
        </Button>
      </Modal.Buttons>
    </Modal>
  )
}
